'use client';

import React, { useRef, useEffect, useMemo } from 'react';
import { useSession } from './SessionProvider';

// ─── 10-20 Electrode positions (normalised 0-1 within head circle) ────────────
// x, y are fractions of the head diameter; origin = top-left of bounding box
const ELECTRODES: {
  id: string;
  label: string;
  x: number; // 0-1 left→right
  y: number; // 0-1 top→bottom
  region: 'prefrontal' | 'frontal' | 'central' | 'parietal' | 'occipital' | 'temporal';
  channel: 'ch1' | 'ch2' | 'ch3' | 'ch4' | 'ch5' | 'ch6' | 'ch7' | 'ch8';
}[] = [
  { id: 'Fp1', label: 'Fp1', x: 0.38, y: 0.12, region: 'prefrontal', channel: 'ch1' },
  { id: 'Fp2', label: 'Fp2', x: 0.62, y: 0.12, region: 'prefrontal', channel: 'ch2' },
  { id: 'F3',  label: 'F3',  x: 0.28, y: 0.30, region: 'frontal',    channel: 'ch3' },
  { id: 'Fz',  label: 'Fz',  x: 0.50, y: 0.27, region: 'frontal',    channel: 'ch3' },
  { id: 'F4',  label: 'F4',  x: 0.72, y: 0.30, region: 'frontal',    channel: 'ch4' },
  { id: 'T7',  label: 'T7',  x: 0.10, y: 0.50, region: 'temporal',   channel: 'ch5' },
  { id: 'C3',  label: 'C3',  x: 0.28, y: 0.50, region: 'central',    channel: 'ch5' },
  { id: 'Cz',  label: 'Cz',  x: 0.50, y: 0.50, region: 'central',    channel: 'ch5' },
  { id: 'C4',  label: 'C4',  x: 0.72, y: 0.50, region: 'central',    channel: 'ch6' },
  { id: 'T8',  label: 'T8',  x: 0.90, y: 0.50, region: 'temporal',   channel: 'ch6' },
  { id: 'P3',  label: 'P3',  x: 0.28, y: 0.70, region: 'parietal',   channel: 'ch7' },
  { id: 'Pz',  label: 'Pz',  x: 0.50, y: 0.73, region: 'parietal',   channel: 'ch7' },
  { id: 'P4',  label: 'P4',  x: 0.72, y: 0.70, region: 'parietal',   channel: 'ch8' },
  { id: 'O1',  label: 'O1',  x: 0.38, y: 0.88, region: 'occipital',  channel: 'ch7' },
  { id: 'Oz',  label: 'Oz',  x: 0.50, y: 0.91, region: 'occipital',  channel: 'ch8' },
  { id: 'O2',  label: 'O2',  x: 0.62, y: 0.88, region: 'occipital',  channel: 'ch8' },
];

// Brain region definitions with display metadata
const REGIONS: {
  id: string;
  label: string;
  subLabel: string;
  color: string;
  glowColor: string;
}[] = [
  { id: 'prefrontal', label: 'Prefrontal',  subLabel: 'Executive / Working Memory', color: '#a78bfa', glowColor: 'rgba(167,139,250,0.5)' },
  { id: 'frontal',    label: 'Frontal',     subLabel: 'Motor Planning / Attention',  color: '#38bdf8', glowColor: 'rgba(56,189,248,0.5)'  },
  { id: 'central',    label: 'Central',     subLabel: 'Sensorimotor',                color: '#34d399', glowColor: 'rgba(52,211,153,0.5)'  },
  { id: 'parietal',   label: 'Parietal',    subLabel: 'Spatial / Integration',       color: '#fbbf24', glowColor: 'rgba(251,191,36,0.5)'  },
  { id: 'occipital',  label: 'Occipital',   subLabel: 'Visual Processing',           color: '#f87171', glowColor: 'rgba(248,113,113,0.5)' },
  { id: 'temporal',   label: 'Temporal',    subLabel: 'Memory / Language',           color: '#F97316', glowColor: 'rgba(249,115,22,0.5)'  },
];

function regionColor(region: string) {
  return REGIONS.find(r => r.id === region)?.color ?? '#94a3b8';
}
function regionGlow(region: string) {
  return REGIONS.find(r => r.id === region)?.glowColor ?? 'rgba(148,163,184,0.4)';
}

// Map band power to an activity level 0-1
function bandToActivity(
  region: string,
  metrics: {
    alphaPower: number; betaPower: number; thetaPower: number;
    deltaPower: number; gammaPower: number; attentionScore: number;
    cognitiveLoadIndex: number;
  }
): number {
  const norm = (v: number, min: number, max: number) => Math.min(1, Math.max(0, (v - min) / (max - min)));
  switch (region) {
    case 'prefrontal': return norm(metrics.cognitiveLoadIndex, 18, 94) * 0.7 + norm(metrics.thetaPower, 6, 42) * 0.3;
    case 'frontal':    return norm(metrics.attentionScore, 22, 96) * 0.6 + norm(metrics.betaPower, 5, 35) * 0.4;
    case 'central':    return norm(metrics.betaPower, 5, 35) * 0.5 + norm(metrics.gammaPower, 2, 18) * 0.5;
    case 'parietal':   return norm(metrics.alphaPower, 8, 38) * 0.6 + norm(metrics.betaPower, 5, 35) * 0.4;
    case 'occipital':  return norm(metrics.alphaPower, 8, 38) * 0.8 + norm(metrics.deltaPower, 4, 28) * 0.2;
    case 'temporal':   return norm(metrics.thetaPower, 6, 42) * 0.6 + norm(metrics.alphaPower, 8, 38) * 0.4;
    default:           return 0.4;
  }
}

// ─── Canvas-based head topomap ────────────────────────────────────────────────
function TopoCanvas({ metrics, size = 300 }: {
  metrics: {
    alphaPower: number; betaPower: number; thetaPower: number;
    deltaPower: number; gammaPower: number; attentionScore: number;
    cognitiveLoadIndex: number;
  };
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const phaseRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const R = size * 0.42; // head radius

    const draw = () => {
      phaseRef.current += 0.018;
      const phase = phaseRef.current;

      ctx.clearRect(0, 0, size, size);

      // ── Head outline ──
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.clip();

      // Background
      ctx.fillStyle = '#060d1a';
      ctx.fillRect(0, 0, size, size);

      // Interpolated heatmap blobs per region
      ELECTRODES.forEach(el => {
        const ex = el.x * size;
        const ey = el.y * size;
        const activity = bandToActivity(el.region, metrics);
        const pulse = 0.85 + Math.sin(phase + el.x * 3 + el.y * 2) * 0.15;
        const finalActivity = Math.min(1, activity * pulse);
        const blobR = R * (0.22 + finalActivity * 0.28);

        const grad = ctx.createRadialGradient(ex, ey, 0, ex, ey, blobR);
        const color = regionColor(el.region);
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        grad.addColorStop(0, `rgba(${r},${g},${b},${0.35 * finalActivity})`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(ex, ey, blobR, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();

      // ── Head circle border ──
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(30,58,95,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Nose indicator ──
      ctx.beginPath();
      ctx.moveTo(cx - 8, cy - R + 4);
      ctx.lineTo(cx, cy - R - 10);
      ctx.lineTo(cx + 8, cy - R + 4);
      ctx.strokeStyle = 'rgba(30,58,95,0.7)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // ── Left/Right ear indicators ──
      [[-1, 1], [1, -1]].forEach(([dir]) => {
        ctx.beginPath();
        ctx.arc(cx + dir * (R + 4), cy, 7, Math.PI * 0.4, Math.PI * 1.6, dir === -1);
        ctx.strokeStyle = 'rgba(30,58,95,0.7)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });

      // ── Electrode dots ──
      ELECTRODES.forEach(el => {
        const ex = el.x * size;
        const ey = el.y * size;
        const activity = bandToActivity(el.region, metrics);
        const pulse = 0.85 + Math.sin(phase * 1.4 + el.x * 4 + el.y * 3) * 0.15;
        const finalActivity = Math.min(1, activity * pulse);
        const dotR = 3.5 + finalActivity * 3;
        const color = regionColor(el.region);
        const hex = color.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);

        // Glow ring
        ctx.beginPath();
        ctx.arc(ex, ey, dotR + 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${0.2 * finalActivity})`;
        ctx.fill();

        // Dot
        ctx.beginPath();
        ctx.arc(ex, ey, dotR, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${0.5 + finalActivity * 0.5})`;
        ctx.fill();

        // Label
        ctx.font = `bold ${9 + finalActivity * 2}px monospace`;
        ctx.fillStyle = `rgba(255,255,255,${0.4 + finalActivity * 0.5})`;
        ctx.textAlign = 'center';
        ctx.fillText(el.label, ex, ey - dotR - 3);
      });

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [metrics, size]);

  return <canvas ref={canvasRef} style={{ borderRadius: '50%' }} />;
}

// ─── Region Activity Bar ──────────────────────────────────────────────────────
function RegionBar({ region, activity }: { region: typeof REGIONS[0]; activity: number }) {
  const pct = Math.round(activity * 100);
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: region.color, boxShadow: `0 0 6px ${region.glowColor}` }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-0.5">
          <span className="text-[11px] font-semibold text-white/80 truncate">{region.label}</span>
          <span className="text-[10px] font-mono ml-1 flex-shrink-0" style={{ color: region.color }}>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${region.color}88, ${region.color})`,
              boxShadow: `0 0 6px ${region.glowColor}`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Dominant Band Badge ──────────────────────────────────────────────────────
function DominantBandBadge({ metrics }: { metrics: { deltaPower: number; thetaPower: number; alphaPower: number; betaPower: number; gammaPower: number } }) {
  const bands = [
    { name: 'Delta', value: metrics.deltaPower, color: '#818cf8', range: '0.5–4 Hz' },
    { name: 'Theta', value: metrics.thetaPower, color: '#38bdf8', range: '4–8 Hz' },
    { name: 'Alpha', value: metrics.alphaPower, color: '#34d399', range: '8–13 Hz' },
    { name: 'Beta',  value: metrics.betaPower,  color: '#fbbf24', range: '13–30 Hz' },
    { name: 'Gamma', value: metrics.gammaPower, color: '#f87171', range: '30–100 Hz' },
  ];
  const dominant = bands.reduce((a, b) => (a.value > b.value ? a : b));
  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-[11px]"
      style={{
        borderColor: `${dominant.color}40`,
        background: `${dominant.color}10`,
      }}
    >
      <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: dominant.color }} />
      <span className="text-white/60">Dominant:</span>
      <span className="font-bold" style={{ color: dominant.color }}>{dominant.name}</span>
      <span className="text-white/40">{dominant.range}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LiveElectrodeMap() {
  const { metrics, status } = useSession();

  const regionActivities = useMemo(() =>
    REGIONS.map(r => ({
      ...r,
      activity: bandToActivity(r.id, metrics),
    })),
    [metrics]
  );

  const mostActive = useMemo(() =>
    [...regionActivities].sort((a, b) => b.activity - a.activity)[0],
    [regionActivities]
  );

  return (
    <div
      className="rounded-xl border p-4 sm:p-5"
      style={{
        background: 'linear-gradient(135deg, rgba(6,13,26,0.95) 0%, rgba(10,20,40,0.95) 100%)',
        borderColor: 'rgba(249,115,22,0.2)',
        boxShadow: '0 0 30px rgba(249,115,22,0.04)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#F97316] animate-pulse" />
          <span className="text-[13px] font-bold text-white/90 tracking-wide uppercase">Live Electrode Map</span>
          <span className="text-[10px] text-white/30 font-mono">10-20 System</span>
        </div>
        <div className="flex items-center gap-2">
          {status === 'streaming' ? (
            <span className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              LIVE
            </span>
          ) : (
            <span className="text-[10px] text-white/30 font-mono uppercase">{status}</span>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* Topomap */}
        <div className="flex flex-col items-center gap-3 flex-shrink-0 mx-auto lg:mx-0">
          <TopoCanvas metrics={metrics} size={260} />
          <DominantBandBadge metrics={metrics} />
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0 w-full">
          {/* Most active region callout */}
          <div
            className="mb-4 px-3 py-2.5 rounded-lg border"
            style={{
              borderColor: `${mostActive.color}35`,
              background: `${mostActive.color}08`,
            }}
          >
            <div className="text-[10px] text-white/40 uppercase tracking-widest mb-0.5">Most Active Region</div>
            <div className="flex items-baseline gap-2">
              <span className="text-[15px] font-bold" style={{ color: mostActive.color }}>{mostActive.label}</span>
              <span className="text-[11px] text-white/50">{mostActive.subLabel}</span>
            </div>
            <div className="text-[10px] text-white/30 mt-0.5 font-mono">{Math.round(mostActive.activity * 100)}% activity</div>
          </div>

          {/* Region activity bars */}
          <div className="space-y-2.5">
            {regionActivities.map(r => (
              <RegionBar key={r.id} region={r} activity={r.activity} />
            ))}
          </div>

          {/* Band power mini-row */}
          <div className="mt-4 pt-3 border-t border-white/5">
            <div className="text-[10px] text-white/30 uppercase tracking-widest mb-2">Band Powers (µV²)</div>
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { name: 'δ', value: metrics.deltaPower, color: '#818cf8' },
                { name: 'θ', value: metrics.thetaPower, color: '#38bdf8' },
                { name: 'α', value: metrics.alphaPower, color: '#34d399' },
                { name: 'β', value: metrics.betaPower,  color: '#fbbf24' },
                { name: 'γ', value: metrics.gammaPower, color: '#f87171' },
              ].map(b => (
                <div key={b.name} className="flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg bg-white/[0.03] border border-white/5">
                  <span className="text-[13px] font-bold" style={{ color: b.color }}>{b.name}</span>
                  <span className="text-[10px] font-mono text-white/60">{b.value.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer note */}
      <p className="mt-4 text-[9px] text-white/20 text-center tracking-wide">
        Colour intensity reflects relative band-power activity per region · Updates every 800 ms
      </p>
    </div>
  );
}
